import { Button, CodeViewer } from '@xhubio-saas/core-frontend/ui-components'
import { ChevronDown, ChevronUp, Download } from 'lucide-react'
import { useState } from 'react'

/** Der Schluessel, unter dem ein Block seinen Auf/Zu-Zustand fuer die Sitzung merkt — je `testId`. */
function openStateKey(testId: string): string {
  return `invoice-validation:open:${testId}`
}

/**
 * Der gemerkte Auf/Zu-Zustand. 🔴 `sessionStorage` kann fehlen oder werfen (privates Fenster, gesperrte
 * Website-Daten) — dann gilt der Standard, die Seite funktioniert ohne Speicher.
 */
function readOpen(testId: string, fallback: boolean): boolean {
  try {
    const stored = sessionStorage.getItem(openStateKey(testId))
    return stored === null ? fallback : stored === '1'
  } catch {
    return fallback
  }
}

function writeOpen(testId: string, open: boolean): void {
  try {
    sessionStorage.setItem(openStateKey(testId), open ? '1' : '0')
  } catch {
    // Ohne Speicher gilt der Zustand nur bis zum Neuladen.
  }
}

import { useTranslation } from 'react-i18next'

/**
 * Ein aufklappbarer Inhaltsblock der Pruefseite — im Stil des „API Request"-Blocks.
 *
 * 🔴 Gleiche Huelle wie `ApiRequestInspector` (invoice-frontend): `rounded-md border`, Kopfzeile als
 * `<button>` mit Titel, Kennchips in `font-mono text-xs` und Auf/Zu-Pfeil rechts. Der Inspector ist
 * fest auf Body/Response/cURL verdrahtet — deshalb ein eigener Block, nicht eine Erweiterung dort.
 *
 * ⚪ Ab `PLAIN_TEXT_FROM_BYTES` keine Hervorhebung: Prism hat keine Virtualisierung und haengt bei
 * Megabyte-XML den Tab auf. Dann steht der Text als `<pre>`, der Download bleibt.
 */
export const PLAIN_TEXT_FROM_BYTES = 2 * 1024 * 1024

/**
 * Hoehe des CodeViewer — Zeilenmass abgeschrieben aus `hoeheFuer` in `ApiRequestInspector.tsx` (dort nicht
 * exportiert): 19 px je Zeile, 56 px Kopf und Polster, ab 180 px.
 *
 * 🔴 Obergrenze 420 px, nicht 700 wie im Inspector (Hallmark-Audit 2026-10-10 v1.71.1, Punkt 5): Inhalt und JSON
 * standen je bis 700 px offen, die Seite wurde ≈ 3 200 px lang. Wer mehr sehen will, hat das Vollbild.
 */
const LINE_PX = 19
const FRAME_PX = 56
const MIN_HEIGHT = 180
const MAX_HEIGHT = 420

function heightFor(content: string): number {
  const lines = content.split('\n').length
  return Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, lines * LINE_PX + FRAME_PX))
}

export interface ContentChip {
  text: string
  mono?: boolean
}

export interface ContentBlockProps {
  title: string
  /**
   * Kurze Angaben neben dem Titel (Dateiname, Groesse, Zeilen …), getrennt durch „·". Leere Eintraege entfallen.
   * `mono` nur fuer Kennungen (Rechnungsnummer) — Dateiname und Zahlen stehen in der Grundschrift.
   */
  chips: (ContentChip | undefined)[]
  /** Der anzuzeigende Text. Fehlt er, steht `hint` im Block. */
  content?: string
  /** Satz statt Inhalt — etwa beim PDF, dessen XML der Server liest, aber nicht mitliefert. */
  hint?: string
  language: 'xml' | 'json'
  /** Dateiname fuer den Download. */
  fileName: string
  /** Groesse in Bytes, an der `PLAIN_TEXT_FROM_BYTES` gemessen wird (bei der Datei: die Dateigroesse). */
  bytes: number
  testId: string
  /**
   * `false`: der Block steht immer offen und hat keinen Auf/Zu-Knopf — als Inhalt eines Werkbank-Tabs, wo der Tab
   * selbst das Auf und Zu ist (Hallmark-Redesign 2026-10-10). Standard `true`: der aufklappbare Block wie bisher.
   */
  collapsible?: boolean
}

export function ContentBlock({
  title,
  chips,
  content,
  hint,
  language,
  fileName,
  bytes,
  testId,
  collapsible = true,
}: ContentBlockProps) {
  const { t } = useTranslation()
  // Standardmaessig offen: der Block ist die Antwort auf „was habe ich hochgeladen?". Einmal zugeklappt, bleibt er
  // es fuer die Sitzung (je Block, Hallmark-Audit 2026-10-10 v1.71.1, Punkt 5).
  const [storedOpen, setOpen] = useState(() => (collapsible ? readOpen(testId, true) : true))
  const open = collapsible ? storedOpen : true
  const toggle = () => {
    setOpen(!open)
    writeOpen(testId, !open)
  }
  const plainText = bytes > PLAIN_TEXT_FROM_BYTES
  const headItems: { kind: 'title' | 'id' | 'text'; text: string }[] = [
    { kind: 'title', text: title },
    ...chips
      .filter((c): c is ContentChip => Boolean(c?.text))
      .map((c) => ({ kind: c.mono ? ('id' as const) : ('text' as const), text: c.text })),
  ]

  const download = () => {
    if (content === undefined) return
    const url = URL.createObjectURL(new Blob([content], { type: 'text/plain' }))
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    a.click()
    URL.revokeObjectURL(url)
  }

  const head = (
    <>
      {/* 🔴 Kein „·" am Zeilenanfang (Hallmark-Audit 2026-10-10 v1.71.3, Punkt 4): Jeder Eintrag traegt seinen
          Trenner LINKS vor sich, im Polster `pl-4`; die Liste ist um dieselbe Breite nach links verschoben (`-ml-4`)
          und die Huelle schneidet ab (`overflow-hidden`). Wer am Anfang einer Zeile steht — der Titel immer, ein
          umgebrochener Kennchip bei 375 px —, hat seinen Trenner im abgeschnittenen Rand. So bricht der Kopf an
          jeder Stelle, ohne dass ein Trenner verwaist, und ohne Messung im Browser. */}
      <div className="min-w-0 overflow-hidden text-left" data-testid={`${testId}-head`}>
        <div className="-ml-4 flex flex-wrap items-baseline gap-y-0.5">
          {headItems.map((item) => (
            // Kennung und Text: die Angaben eines Blocks sind verschieden (Name, Groesse, Zahl).
            <span
              key={`${item.kind}-${item.text}`}
              className={`relative min-w-0 pl-4 ${item.kind === 'title' ? '' : 'text-xs font-normal text-muted-foreground tabular-nums'}`}
              data-testid={item.kind === 'title' ? undefined : `${testId}-chip`}
            >
              <span
                aria-hidden="true"
                className="absolute left-0 w-4 text-center font-normal text-muted-foreground"
              >
                ·
              </span>
              {/* Ein Dateiname ohne Trennstelle bricht notfalls im Wort, statt ueberzustehen. */}
              <span className={`[overflow-wrap:anywhere] ${item.kind === 'id' ? 'font-mono' : ''}`}>
                {item.text}
              </span>
            </span>
          ))}
        </div>
      </div>
    </>
  )

  return (
    <div className="rounded-md border border-border bg-card" data-testid={testId}>
      {collapsible ? (
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-sm font-medium transition-colors hover:bg-muted/50 motion-reduce:transition-none"
          aria-expanded={open}
          onClick={toggle}
        >
          {head}
          {open ? (
            <ChevronUp className="h-4 w-4 shrink-0" />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0" />
          )}
        </button>
      ) : (
        <div className="flex w-full items-center gap-3 px-4 py-3 text-sm font-medium">{head}</div>
      )}

      {open && (
        // 🔴 Kein Polster um den CodeViewer: er sitzt buendig unter der Trennlinie, ohne eigenen Rahmen —
        // sonst Karte in Karte (Hallmark-Audit 2026-10-10, Punkt 5). Hinweis und Klartext behalten ihr `p-4`.
        <div
          className={`border-t border-border ${content !== undefined && !plainText ? '' : 'p-4'}`}
        >
          {content === undefined ? (
            <p className="text-sm text-muted-foreground" data-testid={`${testId}-hint`}>
              {hint}
            </p>
          ) : plainText ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground">
                  {t(
                    'invoiceValidation.content.klartext',
                    'Über 2 MB — ohne Hervorhebung angezeigt.'
                  )}
                </p>
                <Button variant="outline" size="sm" onClick={download}>
                  <Download className="h-4 w-4" />
                  {t('invoiceValidation.content.herunterladen', 'Herunterladen')}
                </Button>
              </div>
              {/* Bewusst gescrollter Teilbereich wie die CodeViewer-Huelle (`MAX_HEIGHT`, 420 px). */}
              <pre
                className="max-h-[420px] overflow-auto rounded-md bg-muted/50 p-3 font-mono text-xs"
                data-testid={`${testId}-plaintext`}
              >
                {content}
              </pre>
            </div>
          ) : (
            <CodeViewer
              // 🔴 Rahmen und Rundung weg: die Huelle ist der Block. Die Kopfzeile des CodeViewer BLEIBT — Falten
              // und Ausklappen aller Abschnitte haengen an seinem inneren Zustand und lassen sich von aussen
              // nicht ausloesen (core-frontend `code-viewer.tsx`: `collapseAll`/`expandAll` ohne Prop). Eigene
              // Knoepfe in der Blockzeile haetten genau diese beiden verloren (Audit-Punkt 5, Minimalloesung).
              className="rounded-none border-0"
              value={content}
              language={language}
              readonly
              // 🔴 Nicht `title`: der steht schon in der Kopfzeile des Blocks — zweimal derselbe Titel
              // uebereinander (Patrick 2026-10-10). Ganz weglassen geht nicht: ohne `label` schreibt der
              // CodeViewer (core-frontend) „Code" hin, und ohne Kopfzeile (`showHeader={false}`) fielen Kopieren,
              // Download, Falten und Vollbild mit weg. Also nennt die Zeile die Sprache — eine neue Auskunft.
              label={language.toUpperCase()}
              filename={fileName}
              height={heightFor(content)}
              showLineNumbers
              expandable
              expandTitle={title}
              foldable
              // Schnellsuche (core-frontend ≥ 5.72.0): Texte aus dem App-Katalog
              searchLabels={{
                placeholder: t('invoiceValidation.content.search.placeholder', 'Suchen…'),
                inputLabel: t('invoiceValidation.content.search.inputLabel', 'Im Code suchen'),
                next: t('invoiceValidation.content.search.next', 'Nächster Treffer'),
                previous: t('invoiceValidation.content.search.previous', 'Vorheriger Treffer'),
                clear: t('invoiceValidation.content.search.clear', 'Suche zurücksetzen'),
                noMatches: t('invoiceValidation.content.search.noMatches', '0 Treffer'),
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}
